//@flow
import React from 'react';
import { type TFunction } from 'react-i18next';
import { type ThemeType } from 'react-css-themr';
import {
    Box,
    Stack,
    Text,
    Icon as NeoIcon,
    Columns,
    Column,
    Button as NeoButton,
    Divider
} from '@locus-taxy/neo-ui';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';

type Props = {
    sortBeats: (selectedSortOption: string, selectedSortOrder: string) => void,
    resetSort: () => void,
    onSortToggle: () => void,
    theme: ThemeType,
    closeSortPopup: () => void,
    t: TFunction
};

type State = {
    generalSortExpanded: boolean,
    selectedSortOption: string,
    selectedSortOrder: string
};

export const SortingOptions = Object.freeze({
    Salesman: 'salesman',
    Day: 'day',
    SalesCategory: 'salesCategory',
    Week: 'week'
});

export const SortPref = Object.freeze({
    ASC: 'ascending',
    DESC: 'descending'
});

class FpBeatSort extends React.Component<Props, State> {
    generalSortingPopupRef: React.RefObject;

    constructor(props) {
        super(props);
        this.state = {
            generalSortExpanded: false,
            selectedSortOption: null,
            selectedSortOrder: null
        };
    }

    onSortApply = () => {
        const { sortBeats } = this.props;
        const { selectedSortOrder, selectedSortOption } = this.state;
        sortBeats(selectedSortOption, selectedSortOrder);
    };

    sortWeekAscending = () => {
        this.setState({
            selectedSortOption: SortingOptions.Week,
            selectedSortOrder: SortPref.ASC
        });
    };

    sortWeekDescending = () => {
        this.setState({
            selectedSortOption: SortingOptions.Week,
            selectedSortOrder: SortPref.DESC
        });
    };

    sortSalesmanAscending = () => {
        this.setState({
            selectedSortOption: SortingOptions.Salesman,
            selectedSortOrder: SortPref.ASC
        });
    };
    sortSalesmanDescending = () => {
        this.setState({
            selectedSortOption: SortingOptions.Salesman,
            selectedSortOrder: SortPref.DESC
        });
    };
    sortDayAscending = () => {
        this.setState({
            selectedSortOption: SortingOptions.Day,
            selectedSortOrder: SortPref.ASC
        });
    };
    sortDayDescending = () => {
        this.setState({
            selectedSortOption: SortingOptions.Day,
            selectedSortOrder: SortPref.DESC
        });
    };
    sortSalesCategoryAscending = () => {
        this.setState({
            selectedSortOption: SortingOptions.SalesCategory,
            selectedSortOrder: SortPref.ASC
        });
    };
    sortSalesCategoryDescending = () => {
        this.setState({
            selectedSortOption: SortingOptions.SalesCategory,
            selectedSortOrder: SortPref.DESC
        });
    };

    resetSort = () => {
        const { resetSort } = this.props;
        this.setState(
            {
                selectedSortOrder: null,
                selectedSortOption: null
            },
            () => {
                resetSort();
            }
        );
    };

    getPopupRef = ref => {
        if (!ref) {
            return null;
        }
        this.generalFilterPopupRef = ref;
    };

    render() {
        const { onSortToggle, theme, closeSortPopup, t } = this.props;
        const { selectedSortOrder, selectedSortOption } = this.state;
        return (
            <Box onClick={onSortToggle}>
                <Stack space="xxSmall">
                    <Box height="56px" p={4} className={theme.filterHeading}>
                        <Columns alignY="center" height="fluid">
                            <Column width="fluid">
                                <Text fontSize={3} fontWeight={600}>
                                    {t('fpPlanView.sortBy')}
                                </Text>
                            </Column>
                            <Column width="content">
                                <NeoIcon
                                    icon="cross"
                                    size="large"
                                    onClick={closeSortPopup}
                                />
                            </Column>
                        </Columns>
                    </Box>

                    <Box className={theme.generalFiltersWrap}>
                        <Stack>
                            <Box p={4} height="48px">
                                <Columns
                                    alignY="center"
                                    height="fluid"
                                    space="small"
                                >
                                    <Column width="fluid">
                                        <Text fontWeight={600} fontSize={2}>
                                            {t('fpPlanView.day')}
                                        </Text>
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_ascending_text"
                                            size="xLarge"
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.ASC &&
                                                selectedSortOption ===
                                                    SortingOptions.Day
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                            onClick={this.sortDayAscending}
                                        />
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_descending_text"
                                            size="xLarge"
                                            onClick={this.sortDayDescending}
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.DESC &&
                                                selectedSortOption ===
                                                    SortingOptions.Day
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                </Columns>
                            </Box>
                            <Divider color="grey.200" />
                            <Box p={4} height="48px">
                                <Columns
                                    space="small"
                                    alignY="center"
                                    height="fluid"
                                >
                                    <Column width="fluid">
                                        <Text fontWeight={600} fontSize={2}>
                                            {t('fpPlanView.week')}
                                        </Text>
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_ascending_text"
                                            size="xLarge"
                                            onClick={this.sortWeekAscending}
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.ASC &&
                                                selectedSortOption ===
                                                    SortingOptions.Week
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_descending_text"
                                            size="xLarge"
                                            onClick={this.sortWeekDescending}
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.DESC &&
                                                selectedSortOption ===
                                                    SortingOptions.Week
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                </Columns>
                            </Box>
                            <Divider color="grey.200" />
                            <Box p={4} height="48px">
                                <Columns
                                    space="small"
                                    alignY="center"
                                    height="fluid"
                                >
                                    <Column width="fluid">
                                        <Text fontWeight={600} fontSize={2}>
                                            {t('fpPlanView.salesman')}
                                        </Text>
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_ascending_text"
                                            size="xLarge"
                                            onClick={this.sortSalesmanAscending}
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.ASC &&
                                                selectedSortOption ===
                                                    SortingOptions.Salesman
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_descending_text"
                                            size="xLarge"
                                            onClick={
                                                this.sortSalesmanDescending
                                            }
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.DESC &&
                                                selectedSortOption ===
                                                    SortingOptions.Salesman
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                </Columns>
                            </Box>
                            <Divider color="grey.200" />
                            <Box p={4} height="48px">
                                <Columns
                                    space="small"
                                    alignY="center"
                                    height="fluid"
                                >
                                    <Column width="fluid">
                                        <Text fontWeight={600} fontSize={2}>
                                            {t('fpPlanView.salesCategory')}
                                        </Text>
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_ascending_text"
                                            size="xLarge"
                                            onClick={
                                                this.sortSalesCategoryAscending
                                            }
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.ASC &&
                                                selectedSortOption ===
                                                    SortingOptions.SalesCategory
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            icon="sort_descending_text"
                                            size="xLarge"
                                            onClick={
                                                this.sortSalesCategoryDescending
                                            }
                                            color={
                                                selectedSortOrder ===
                                                    SortPref.DESC &&
                                                selectedSortOption ===
                                                    SortingOptions.SalesCategory
                                                    ? 'blue.500'
                                                    : 'grey.500'
                                            }
                                        />
                                    </Column>
                                </Columns>
                            </Box>
                            <Divider color="grey.200" />
                        </Stack>
                    </Box>
                    <Box className={theme.filterFooter} p={2}>
                        <Columns
                            align="right"
                            alignY="center"
                            space="small"
                            wrap
                        >
                            <Column width="content">
                                <NeoButton
                                    palette="primary"
                                    onClick={this.resetSort}
                                >
                                    {t('fpPlanView.clear')}
                                </NeoButton>
                            </Column>
                            <Column width="content">
                                <NeoButton
                                    appearance="solid"
                                    palette="primary"
                                    onClick={this.onSortApply}
                                >
                                    {t('fpPlanView.apply')}
                                </NeoButton>
                            </Column>
                        </Columns>
                    </Box>
                </Stack>
            </Box>
        );
    }
}

export default HandleOutsideClick(KeyEventHandler(FpBeatSort));
